"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardRenderer = void 0;
var tslib_1 = require("tslib");
// (C) 2020 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_theme_provider_1 = require("@gooddata/sdk-ui-theme-provider");
var internal_1 = require("../internal");
var DashboardWidgetRenderer_1 = require("./DashboardWidgetRenderer/DashboardWidgetRenderer");
var contexts_1 = require("./contexts");
/**
 * Ensure that areObjRefsEqual() and other predicates will be working with uncontrolled user ref inputs in custom layout transformation and/or custom widget/item renderers
 */
var polluteWidgetRefsWithBothIdAndUri = function (getInsightByRef) {
    return function (item) {
        return item.widget(function (c) {
            var updatedContent = tslib_1.__assign({}, c);
            if (sdk_backend_spi_1.isWidget(updatedContent)) {
                updatedContent.ref = tslib_1.__assign(tslib_1.__assign({}, updatedContent.ref), { uri: sdk_backend_spi_1.widgetUri(updatedContent), identifier: sdk_backend_spi_1.widgetId(updatedContent) });
            }
            if (sdk_backend_spi_1.isInsightWidget(updatedContent)) {
                var insight = getInsightByRef(updatedContent.insight);
                updatedContent.insight = tslib_1.__assign(tslib_1.__assign({}, updatedContent.insight), { uri: sdk_model_1.insightUri(insight), identifier: sdk_model_1.insightId(insight) });
            }
            return updatedContent;
        });
    };
};
var validateItemsSize = function (getInsightByRef, enableKDWidgetCustomHeight) {
    return function (item) {
        var widget = item.facade().widget();
        if (sdk_backend_spi_1.isInsightWidget(widget)) {
            var insight = getInsightByRef(widget.insight);
            var currentWidth = item.facade().size().xl.gridWidth;
            var currentHeight = item.facade().size().xl.gridHeight;
            var _a = internal_1.validateDashboardLayoutWidgetSize(currentWidth, currentHeight, "insight", insight, { enableKDWidgetCustomHeight: enableKDWidgetCustomHeight }), validWidth = _a.validWidth, validHeight = _a.validHeight;
            var validatedItem = item;
            if (currentWidth !== validWidth) {
                validatedItem = validatedItem.size({
                    xl: tslib_1.__assign(tslib_1.__assign({}, validatedItem.facade().size().xl), { gridWidth: validWidth }),
                });
            }
            if (currentHeight !== validHeight) {
                validatedItem = validatedItem.size({
                    xl: tslib_1.__assign(tslib_1.__assign({}, validatedItem.facade().size().xl), { gridHeight: validHeight }),
                });
            }
            return validatedItem;
        }
    };
};
exports.DashboardRenderer = react_1.memo(function DashboardRenderer(_a) {
    var dashboardLayout = _a.dashboardLayout, filters = _a.filters, onFiltersChange = _a.onFiltersChange, filterContext = _a.filterContext, backend = _a.backend, workspace = _a.workspace, drillableItems = _a.drillableItems, onDrill = _a.onDrill, ErrorComponent = _a.ErrorComponent, onError = _a.onError, LoadingComponent = _a.LoadingComponent, className = _a.className, getVisType = _a.getVisType, getInsightByRef = _a.getInsightByRef, widgetRenderer = _a.widgetRenderer, areSectionHeadersEnabled = _a.areSectionHeadersEnabled, dashboardRef = _a.dashboardRef, transformLayout = _a.transformLayout;
    var alerts = contexts_1.useAlerts().alerts;
    var isThemeLoading = sdk_ui_theme_provider_1.useThemeIsLoading();
    if (isThemeLoading) {
        // do not render the dashboard until you have the theme to avoid flash of un-styled content
        return react_1.default.createElement(LoadingComponent, null);
    }
    var userWorkspaceSettings = contexts_1.useUserWorkspaceSettings();
    var getWidgetAlert = function (widgetRef) { return alerts === null || alerts === void 0 ? void 0 : alerts.find(function (alert) { return sdk_model_1.areObjRefsEqual(alert.widget, widgetRef); }); };
    var selectAllItemsWithInsights = function (items) {
        return items.filter(function (item) { return item.isInsightWidgetItem(); });
    };
    var commonLayoutBuilder = internal_1.DashboardLayoutBuilder.for(dashboardLayout).modifySections(function (section) {
        return section
            .modifyItems(polluteWidgetRefsWithBothIdAndUri(getInsightByRef))
            .modifyItems(validateItemsSize(getInsightByRef, userWorkspaceSettings.enableKDWidgetCustomHeight), selectAllItemsWithInsights);
    });
    var transformedLayout = transformLayout
        ? transformLayout(commonLayoutBuilder, {
            getWidgetAlert: getWidgetAlert,
            getInsight: getInsightByRef,
            filters: filters,
        }).build()
        : commonLayoutBuilder.build();
    return (react_1.default.createElement(internal_1.DashboardLayout, { layout: transformedLayout, itemKeyGetter: function (keyGetterProps) {
            var widget = keyGetterProps.item.widget();
            if (sdk_backend_spi_1.isWidget(widget)) {
                return sdk_model_1.objRefToString(widget.ref);
            }
            return keyGetterProps.item.index().toString();
        }, widgetRenderer: function (renderProps) {
            var item = renderProps.item, screen = renderProps.screen, DefaultWidgetRenderer = renderProps.DefaultWidgetRenderer;
            var visType;
            var widgetType;
            var insight;
            var content;
            var widget = item.widget();
            if (sdk_backend_spi_1.isDashboardLayout(widget)) {
                throw new sdk_backend_spi_1.UnexpectedError("Nested layouts not yet supported.");
            }
            if (sdk_backend_spi_1.isWidget(widget)) {
                visType = getVisType(widget);
                widgetType = sdk_backend_spi_1.widgetType(widget);
            }
            if (sdk_backend_spi_1.isInsightWidget(widget)) {
                insight = getInsightByRef(widget.insight);
                content = insight;
            }
            if (sdk_backend_spi_1.isKpiWidget(widget)) {
                content = widget.kpi;
            }
            var currentSize = item.size()[screen];
            var minHeight = internal_1.getDashboardLayoutItemHeight(currentSize) ||
                (!currentSize.heightAsRatio
                    ? internal_1.getDashboardLayoutWidgetDefaultHeight(userWorkspaceSettings, widgetType, content)
                    : undefined);
            var height = currentSize.heightAsRatio && !currentSize.gridHeight
                ? internal_1.getDashboardLayoutItemHeightForRatioAndScreen(currentSize, screen)
                : undefined;
            var allowOverflow = !!currentSize.heightAsRatio;
            var computedRenderProps = {
                allowOverflow: allowOverflow,
                minHeight: minHeight,
                height: height,
            };
            var alert = sdk_backend_spi_1.isWidget(widget)
                ? alerts === null || alerts === void 0 ? void 0 : alerts.find(function (alert) { return sdk_model_1.areObjRefsEqual(alert.widget, widget.ref); }) : undefined;
            var widgetRenderProps = {
                insight: insight,
                dashboardRef: dashboardRef,
                ErrorComponent: ErrorComponent,
                LoadingComponent: LoadingComponent,
                drillableItems: drillableItems,
                filters: filters,
                onFiltersChange: onFiltersChange,
                filterContext: filterContext,
                onDrill: onDrill,
                onError: onError,
                workspace: workspace,
                backend: backend,
                widget: sdk_backend_spi_1.isWidget(widget) ? widget : undefined,
                visType: visType,
                screen: screen,
                alert: alert,
            };
            var renderedWidget = sdk_backend_spi_1.isWidget(widget) ? (react_1.default.createElement(DashboardWidgetRenderer_1.DashboardWidgetRenderer, tslib_1.__assign({}, widgetRenderProps))) : null;
            var predicates = {
                isCustomWidget: function () { return !sdk_backend_spi_1.isDashboardWidget(widget); },
                isWidgetWithRef: function (ref) { return sdk_backend_spi_1.isWidget(widget) && sdk_model_1.areObjRefsEqual(ref, widget.ref); },
                isWidgetWithKpiRef: function (ref) {
                    return sdk_backend_spi_1.isWidget(widget) && widget.type === "kpi" && sdk_model_1.areObjRefsEqual(ref, widget.ref);
                },
                isWidgetWithKpiType: function (comparisonType) {
                    return sdk_backend_spi_1.isWidget(widget) &&
                        widget.type === "kpi" &&
                        comparisonType === widget.kpi.comparisonType;
                },
                isWidgetWithInsightRef: function (ref) {
                    return sdk_backend_spi_1.isWidget(widget) && widget.type === "insight" && sdk_model_1.areObjRefsEqual(ref, widget.insight);
                },
                isWidgetWithInsightType: function (type) {
                    return sdk_backend_spi_1.isWidget(widget) && widget.type === "insight" && type === visType;
                },
            };
            var commonCustomWidgetRenderProps = {
                ErrorComponent: ErrorComponent,
                LoadingComponent: LoadingComponent,
                predicates: predicates,
                widget: sdk_backend_spi_1.isWidget(widget) ? widget : undefined,
                renderedWidget: renderedWidget,
                filters: filters,
                customWidget: !sdk_backend_spi_1.isDashboardWidget(widget) ? widget : undefined,
            };
            var customWidgetRendererProps = tslib_1.__assign(tslib_1.__assign({}, commonCustomWidgetRenderProps), { insight: insight,
                alert: alert });
            var className = userWorkspaceSettings.enableKDWidgetCustomHeight
                ? "custom-height"
                : undefined;
            return (react_1.default.createElement(DefaultWidgetRenderer, tslib_1.__assign({}, renderProps, computedRenderProps, { className: className }), widgetRenderer ? widgetRenderer(customWidgetRendererProps) : renderedWidget));
        }, className: className, 
        // When section headers are enabled, use default DashboardLayout rowHeaderRenderer.
        // When turned off, render nothing.
        sectionHeaderRenderer: areSectionHeadersEnabled ? undefined : function () { return null; }, enableCustomHeight: userWorkspaceSettings.enableKDWidgetCustomHeight }));
});
//# sourceMappingURL=DashboardRenderer.js.map